/**
 * F-70 language list for the translate dialog's From/To selects — PURE data
 * (no React, no Yjs).
 *
 * The tags are what we send the model as `from`/`to` and what we persist in the
 * column's `ColumnTranslation` mapping, so they are BCP-47 subtags rather than
 * display names: stable across UI copy changes, and unambiguous for the model
 * ("zh-Hant" vs "zh-Hans" is a distinction "Chinese" cannot make).
 *
 * The list is deliberately a curated short list, not the full BCP-47 registry:
 * this is an internal tool translating scripts for ~30 people, and a 8000-entry
 * dropdown is worse than a 20-entry one. Adding a language = one line here —
 * nothing else in the pipeline enumerates languages (the server passes the tag
 * through to OpenRouter, and the mapping stores whatever string it is given).
 */
import { TRANSLATION_AUTO_LANG } from "@kola/shared";

export interface Language {
  /** BCP-47 tag sent to the provider and persisted in the mapping. */
  tag: string;
  /** English display name for the select. */
  label: string;
}

/** Selectable languages, in the alphabetical order the selects render them. */
export const TRANSLATE_LANGUAGES: readonly Language[] = [
  { tag: "ar", label: "Arabic" },
  { tag: "zh-Hans", label: "Chinese (Simplified)" },
  { tag: "zh-Hant", label: "Chinese (Traditional)" },
  { tag: "nl", label: "Dutch" },
  { tag: "en", label: "English" },
  { tag: "fr", label: "French" },
  { tag: "de", label: "German" },
  { tag: "hi", label: "Hindi" },
  { tag: "id", label: "Indonesian" },
  { tag: "it", label: "Italian" },
  { tag: "ja", label: "Japanese" },
  { tag: "ko", label: "Korean" },
  { tag: "pl", label: "Polish" },
  { tag: "pt-BR", label: "Portuguese (Brazil)" },
  { tag: "pt-PT", label: "Portuguese (Portugal)" },
  { tag: "ru", label: "Russian" },
  { tag: "es", label: "Spanish" },
  { tag: "es-419", label: "Spanish (Latin America)" },
  { tag: "th", label: "Thai" },
  { tag: "tr", label: "Turkish" },
  { tag: "vi", label: "Vietnamese" },
];

/** What "auto" renders as in the From select (the F-70 default). */
export const AUTO_DETECT_LABEL = "Auto-detect";

/**
 * Display name for a stored tag. Falls back to the tag itself so a mapping
 * written before a language was removed from the list (or by a future API
 * caller) still renders as *something* readable instead of an empty select.
 */
export function languageLabel(tag: string): string {
  if (tag === TRANSLATION_AUTO_LANG) return AUTO_DETECT_LABEL;
  return TRANSLATE_LANGUAGES.find((language) => language.tag === tag)?.label ?? tag;
}

/**
 * The From/To options to render, given the currently-selected tag: the curated
 * list, plus the selected tag itself when it is not in it (an unknown tag from
 * an existing mapping must stay selected, not silently become another
 * language). `includeAuto` adds the "Auto-detect" entry — From only; F-70 has
 * no auto TARGET.
 */
export function languageOptions(selected: string, includeAuto: boolean): Language[] {
  const options: Language[] = includeAuto
    ? [{ tag: TRANSLATION_AUTO_LANG, label: AUTO_DETECT_LABEL }, ...TRANSLATE_LANGUAGES]
    : [...TRANSLATE_LANGUAGES];
  if (selected !== "" && !options.some((language) => language.tag === selected)) {
    options.push({ tag: selected, label: selected });
  }
  return options;
}
